var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useCallback } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import { compose, filter, map, pathOr, prepend } from 'ramda';
import useDebouncedFunction from 'utils/hooks/useDebouncedFunction';
import { withShowAllOptions } from 'components/OrganizationSelect/helpers';
import { cursorFetchMore, getNodes, getPageInfo } from 'utils/paginationHelper';
import { NODES_PATH } from 'constants/dataPaths';
import { FIRST_25_VARIABLES } from 'constants/pagination';
import ORGANIZATIONS_FOR_SELECT from 'apollo/queries/ORGANIZATIONS_FOR_SELECT';
import ORGANIZATION_FOR_SELECT from 'apollo/queries/ORGANIZATION_FOR_SELECT';
import Organization from 'model/organizations/organization';
export var getOrganizationOptions = function (_a) {
    var defaultValue = _a.defaultValue, organizationsData = _a.organizationsData, showAnyOption = _a.showAnyOption, mode = _a.mode;
    var organizationOptions = compose(map(function (item) { return new Organization(item).getSelectOption; }), getNodes(['organizationOptions']))(organizationsData);
    if (defaultValue) {
        if (mode === 'multiple') {
            var defaultOptions_1 = defaultValue.map(function (item) { return new Organization(item).getSelectOption; });
            var filteredOptions = organizationOptions.filter(function (_a) {
                var value = _a.value;
                return !defaultOptions_1.find(function (item) { return (item === null || item === void 0 ? void 0 : item.value) === value; });
            });
            return __spreadArray(__spreadArray([], defaultOptions_1, true), filteredOptions, true);
        }
        var organization_1 = new Organization(pathOr(null, ['organizationOption'], defaultValue));
        return compose(withShowAllOptions(showAnyOption), prepend(organization_1.getSelectOption), filter(function (item) { return ((item === null || item === void 0 ? void 0 : item.value) !== organization_1.id); }))(organizationOptions);
    }
    return organizationOptions;
};
var useOrganizationSelect = function (_a) {
    var _b;
    var organizationId = _a.organizationId, showAnyOption = _a.showAnyOption, mode = _a.mode;
    var _c = useLazyQuery(ORGANIZATIONS_FOR_SELECT), loadOrganizations = _c[0], _d = _c[1], organizationsLoading = _d.loading, organizationsData = _d.data, fetchMore = _d.fetchMore;
    var variables = mode === 'multiple' ? { ids: organizationId } : { id: organizationId };
    var defaultValueQuery = mode === 'multiple' ? ORGANIZATIONS_FOR_SELECT : ORGANIZATION_FOR_SELECT;
    var _e = useQuery(defaultValueQuery, {
        variables: variables,
        skip: !(organizationId === null || organizationId === void 0 ? void 0 : organizationId.length)
    }), organizationLoading = _e.loading, organizationData = _e.data;
    var defaultValue = mode === 'multiple'
        ? (_b = organizationData === null || organizationData === void 0 ? void 0 : organizationData.organizationOptions) === null || _b === void 0 ? void 0 : _b.nodes
        : organizationData;
    var doSearch = useCallback(function (search) {
        if (!search)
            return false;
        return loadOrganizations({
            variables: __assign({ search: search }, FIRST_25_VARIABLES)
        });
    }, [loadOrganizations]);
    var debouncedSearch = useDebouncedFunction(doSearch, 500);
    var onPopupScroll = function (event) {
        var target = event.target;
        if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
            var pageInfo = getPageInfo(['organizationOptions'])(organizationsData);
            if (!pageInfo.hasNextPage)
                return;
            cursorFetchMore(fetchMore)({
                dataPath: ['organizationOptions'],
                dataType: NODES_PATH,
                pageInfo: pageInfo
            });
        }
    };
    var organizationOptions = getOrganizationOptions({
        defaultValue: defaultValue,
        organizationsData: organizationsData,
        showAnyOption: showAnyOption,
        mode: mode
    });
    return {
        functions: {
            debouncedSearch: debouncedSearch,
            onPopupScroll: onPopupScroll
        },
        data: {
            organizationOptions: organizationOptions,
            loading: organizationLoading || organizationsLoading
        }
    };
};
export default useOrganizationSelect;
